import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

// The Launch Media player's look and behavior (packages/launch-player), as a React component for a
// private video: same controls, same keys, same fade, but it plays one signed file directly (no
// streaming versions), so the page can still read the picture when someone comments on a frame.

const fmt = (t: number) => {
  if (!isFinite(t)) return "0:00";
  t = Math.floor(t);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  return `${h ? `${h}:${String(m).padStart(2, "0")}` : m}:${String(s).padStart(2, "0")}`;
};

export function LaunchPlayer({ src, itemId, label, onError, current = true }: { src: string; itemId: string; label: string; onError?: () => void; current?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [fresh, setFresh] = useState(true);
  const [idle, setIdle] = useState(false);
  const [spin, setSpin] = useState(false);
  const [time, setTime] = useState({ t: 0, d: 0, b: 0 });
  const [vol, setVol] = useState({ v: 1, muted: false });
  const [menu, setMenu] = useState(false);
  const [rate, setRate] = useState(1);
  const [full, setFull] = useState(false);
  const [hover, setHover] = useState<{ f: number; text: string } | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const drag = useRef(false);

  const wake = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => { if (video.current && !video.current.paused) setIdle(true); }, 2500);
  }, []);
  useEffect(() => () => clearTimeout(idleTimer.current), []);

  const toggle = () => { const v = video.current!; if (v.paused || v.ended) Promise.resolve(v.play()).catch(() => {}); else v.pause(); };
  const paint = () => {
    const v = video.current!, d = v.duration || 0, b = v.buffered;
    setTime({ t: v.currentTime || 0, d, b: b.length && d ? (b.end(b.length - 1) / d) * 100 : 0 });
  };
  const at = (e: React.PointerEvent<HTMLDivElement>) => { const r = e.currentTarget.getBoundingClientRect(); return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)); };
  const seekTo = (f: number) => { const v = video.current!; if (v.duration) { v.currentTime = f * v.duration; paint(); } };

  useEffect(() => {
    const on = () => setFull(!!(document.fullscreenElement || (document as any).webkitFullscreenElement));
    document.addEventListener("fullscreenchange", on); document.addEventListener("webkitfullscreenchange", on);
    const close = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest?.(".lp-menuwrap")) setMenu(false); };
    document.addEventListener("click", close);
    return () => { document.removeEventListener("fullscreenchange", on); document.removeEventListener("webkitfullscreenchange", on); document.removeEventListener("click", close); };
  }, []);

  const goFull = () => {
    const el = box.current as any, v = video.current as any;
    if (document.fullscreenElement || (document as any).webkitFullscreenElement) (document.exitFullscreen || (document as any).webkitExitFullscreen).call(document);
    else if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };
  const muted = vol.muted || vol.v === 0;
  const opt = (text: string, checked: boolean, pick: () => void): ReactNode => (
    <button key={text} type="button" className="lp-opt" role="menuitemradio" aria-checked={checked} onClick={(e) => { e.stopPropagation(); pick(); setMenu(false); }}>{text}</button>
  );

  return (
    <div ref={box} className={`lp${playing ? " playing" : ""}${fresh ? " fresh" : ""}${idle ? " idle" : ""}${muted ? " muted" : ""}${full ? " full" : ""}`} tabIndex={0}
      onPointerMove={wake} onPointerDown={wake} onFocus={wake} onPointerLeave={() => { if (video.current && !video.current.paused) setIdle(true); }}
      onKeyDown={(e) => {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === "INPUT" || (tag === "BUTTON" && (e.key === " " || e.key === "Enter")) || (e.target as HTMLElement).getAttribute("role") === "slider") return;
        const v = video.current!;
        if (e.key === " " || e.key === "k") { toggle(); e.preventDefault(); }
        else if (e.key === "f") goFull();
        else if (e.key === "m") v.muted = !v.muted;
        else if (e.key === "ArrowRight") v.currentTime += 5;
        else if (e.key === "ArrowLeft") v.currentTime -= 5;
      }}>
      {/* crossOrigin lets a still be grabbed from the picture (the bucket allows this app's address) */}
      <video ref={video} className="lp-video" {...(current ? { "data-item": itemId } : {})} src={src} playsInline preload="metadata" crossOrigin="anonymous" aria-label={label}
        onClick={toggle} onError={onError}
        onPlay={() => { setPlaying(true); setFresh(false); wake(); }}
        onPause={() => { setPlaying(false); setIdle(false); }}
        onWaiting={() => setSpin(true)} onPlaying={() => setSpin(false)} onCanPlay={() => setSpin(false)}
        onTimeUpdate={paint} onDurationChange={paint} onProgress={paint} onLoadedMetadata={paint}
        onVolumeChange={() => { const v = video.current!; setVol({ v: v.volume, muted: v.muted }); }} />
      {!playing && <button className="lp-big" type="button" aria-label="Play video" onClick={(e) => { e.stopPropagation(); toggle(); }}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg></button>}
      {spin && <div className="lp-spin" />}
      <div className="lp-bar">
        <div className="lp-track" role="slider" aria-label="Seek" tabIndex={0} aria-valuemin={0} aria-valuemax={100} aria-valuenow={time.d ? Math.round((time.t / time.d) * 100) : 0} aria-valuetext={fmt(time.t)}
          onPointerDown={(e) => { drag.current = true; e.currentTarget.setPointerCapture(e.pointerId); seekTo(at(e)); }}
          onPointerMove={(e) => { const f = at(e); if (time.d) setHover({ f, text: fmt(f * time.d) }); if (drag.current) seekTo(f); }}
          onPointerLeave={() => setHover(null)} onPointerUp={() => { drag.current = false; }}
          onKeyDown={(e) => { const v = video.current!; if (e.key === "ArrowRight") { v.currentTime += 5; e.preventDefault(); } if (e.key === "ArrowLeft") { v.currentTime -= 5; e.preventDefault(); } }}>
          <div className="lp-buffered" style={{ width: `${time.b}%` }} />
          <div className="lp-played" style={{ width: time.d ? `${(time.t / time.d) * 100}%` : 0 }}><span className="lp-knob" /></div>
          {hover && <div className="lp-hovertime" style={{ left: `${hover.f * 100}%` }}>{hover.text}</div>}
        </div>
        <div className="lp-row">
          <button className="lp-btn" type="button" aria-label={playing ? "Pause" : "Play"} onClick={toggle}>
            <svg viewBox="0 0 24 24" className="i-play"><path d="M8 5.5v13l11-6.5z" /></svg><svg viewBox="0 0 24 24" className="i-pause"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /></svg>
          </button>
          <div className="lp-vol">
            <button className="lp-btn" type="button" aria-label={vol.muted ? "Unmute" : "Mute"} onClick={() => { const v = video.current!; v.muted = !v.muted; if (!v.muted && v.volume === 0) v.volume = 1; }}>
              <svg viewBox="0 0 24 24" className="i-vol"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" /><path d="M15 9a4 4 0 0 1 0 6M17.5 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <svg viewBox="0 0 24 24" className="i-muted"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" /><path d="M15.5 9.5l5 5M20.5 9.5l-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
            </button>
            <input className="lp-volslider" type="range" min={0} max={1} step={0.05} value={vol.muted ? 0 : vol.v} aria-label="Volume"
              onChange={(e) => { const v = video.current!; v.volume = Number(e.target.value); v.muted = v.volume === 0; }} />
          </div>
          <span className="lp-time">{fmt(time.t)} / {fmt(time.d)}</span>
          <span className="lp-grow" />
          <div className="lp-menuwrap">
            <button className="lp-btn" type="button" aria-label="Settings" aria-haspopup="true" aria-expanded={menu} onClick={(e) => { e.stopPropagation(); setMenu(!menu); }}>
              <svg viewBox="0 0 24 24"><path d="M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8zm8.2 4.6l-1.6-.3a6.8 6.8 0 0 1-.6 1.5l.9 1.4-1.6 1.6-1.4-.9c-.5.3-1 .5-1.5.6l-.3 1.6h-2.2l-.3-1.6a6.8 6.8 0 0 1-1.5-.6l-1.4.9-1.6-1.6.9-1.4a6.8 6.8 0 0 1-.6-1.5l-1.6-.3v-2.2l1.6-.3c.1-.5.3-1 .6-1.5l-.9-1.4 1.6-1.6 1.4.9c.5-.3 1-.5 1.5-.6l.3-1.6h2.2l.3 1.6c.5.1 1 .3 1.5.6l1.4-.9 1.6 1.6-.9 1.4c.3.5.5 1 .6 1.5l1.6.3z" /></svg>
            </button>
            {menu && (
              <div className="lp-menu">
                <div className="lp-menu-h">Speed</div>
                <div>{[0.5, 1, 1.25, 1.5, 2].map((r) => opt(r === 1 ? "Normal" : `${r}×`, rate === r, () => { video.current!.playbackRate = r; setRate(r); }))}</div>
              </div>
            )}
          </div>
          <button className="lp-btn" type="button" aria-label={full ? "Exit full screen" : "Full screen"} onClick={goFull}>
            <svg viewBox="0 0 24 24" className="i-full"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
            <svg viewBox="0 0 24 24" className="i-exit"><path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
